import React from 'react';
import { motion } from 'framer-motion';
import { MoonIcon, ScaleIcon, CoffeeIcon } from 'lucide-react';
export function ProblemSection() {
  return <section className="bg-white py-16 md:py-24" aria-labelledby="problem-heading" data-id="element-431">
      <div className="max-w-5xl mx-auto px-4" data-id="element-432">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-433">
          <p className="font-sans text-terracotta text-xs font-700 tracking-[0.2em] uppercase mb-4" data-id="element-434">
            Does This Sound Familiar?
          </p>
          <h2 id="problem-heading" className="font-serif text-forest text-3xl md:text-4xl font-700 leading-tight mb-6 max-w-3xl mx-auto" data-id="element-435">
            You're Doing Everything Right. So Why Is Your Body Fighting Back?
          </h2>
          <p className="font-sans text-stone-600 text-lg leading-relaxed max-w-2xl mx-auto" data-id="element-436">
            You're eating 1,200 calories. You're walking 10,000 steps. You've
            cut out the bread, the wine, and the joy. And yet... the scale
            hasn't moved. In fact, your pants feel tighter around the waist than
            they did last month.
          </p>
        </motion.div>

        {/* Bold Callout */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="bg-forest rounded-2xl px-8 py-8 text-center mb-14" data-id="element-437">
          <p className="font-serif text-cream text-xl md:text-2xl font-600 italic leading-snug" data-id="element-438">
            "If you're a woman over 50, you aren't failing your diet.
            <br className="hidden md:block" data-id="element-439" />
            Your diet is failing your biology."
          </p>
        </motion.div>

        {/* Pain Point Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14" data-id="element-440">
          <motion.article initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="bg-cream rounded-2xl p-7 shadow-sm border border-sage/20" data-id="element-441">
            <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center mb-4" data-id="element-442">
              <MoonIcon className="w-6 h-6 text-forest" aria-hidden="true" data-id="element-443" />
            </div>
            <h3 className="font-serif text-forest text-xl font-700 mb-3" data-id="element-444">
              The 3 AM Wake-Up
            </h3>
            <p className="font-sans text-stone-600 text-base leading-relaxed" data-id="element-445">
              You wake at 3am, mind racing. You don't know it yet, but this is
              your cortisol spiking — and it's storing fat while you try to
              sleep.
            </p>
          </motion.article>

          <motion.article initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="bg-cream rounded-2xl p-7 shadow-sm border border-sage/20" data-id="element-446">
            <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center mb-4" data-id="element-447">
              <ScaleIcon className="w-6 h-6 text-forest" aria-hidden="true" data-id="element-448" />
            </div>
            <h3 className="font-serif text-forest text-xl font-700 mb-3" data-id="element-449">
              The Stubborn Scale
            </h3>
            <p className="font-sans text-stone-600 text-base leading-relaxed" data-id="element-450">
              You've eaten less than ever. The scale won't budge. After 50,
              "calories in vs. calories out" is the wrong equation entirely.
            </p>
          </motion.article>

          <motion.article initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="bg-cream rounded-2xl p-7 shadow-sm border border-sage/20" data-id="element-451">
            <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center mb-4" data-id="element-452">
              <CoffeeIcon className="w-6 h-6 text-forest" aria-hidden="true" data-id="element-453" />
            </div>
            <h3 className="font-serif text-forest text-xl font-700 mb-3" data-id="element-454">
              The Morning Sabotage
            </h3>
            <p className="font-sans text-stone-600 text-base leading-relaxed" data-id="element-455">
              Your "healthy" morning coffee ritual is spiking your stress
              hormones before 9am — and locking fat directly into your
              midsection.
            </p>
          </motion.article>
        </div>

        {/* Science Callout */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="border-l-4 border-terracotta bg-terracotta/5 rounded-r-xl px-7 py-6 max-w-3xl mx-auto" data-id="element-456">
          <p className="font-sans text-stone-700 text-base md:text-lg leading-relaxed" data-id="element-457">
            <span className="font-700 text-forest" data-id="element-458">
              After 50, your body stops listening to calories.
            </span>{' '}
            It starts listening to ONE thing:{' '}
            <span className="font-700 text-terracotta" data-id="element-459">Cortisol.</span> When
            stress hormones are high, your body enters "Survival Mode," locking
            fat into your midsection to protect your organs.
          </p>
        </motion.div>
      </div>
    </section>;
}